import React, { useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import DoNotDisturbOnOutlinedIcon from '@mui/icons-material/DoNotDisturbOnOutlined';
import { useSetAtom } from 'jotai';
import { useParams } from 'react-router';
import TableStyles from '../../documents/Table.module.css';
import { LinkTableStyled } from '../../common/StyledComponents';
import { validationDismissalDialogAtom } from '../../../atoms/spreadsheet-atoms';
import { ValidationDismissalReason } from '../../../types/validation-types';

interface TransformedValidationErrorRow {
  id: number;
  extractionId: string;
  field: string;
  errorDetail?: string;
  error?: string;
  dataset?: string;
  dismissalReason?: ValidationDismissalReason | null;
}

interface TransformedValidationErrorsTableProps {
  errors: TransformedValidationErrorRow[];
  totalCount: number;
  dpid: string | undefined;
  extractionBatchId: string;
}

const TransformedValidationErrorsTable: React.FC<TransformedValidationErrorsTableProps> = ({
  errors,
  totalCount,
  dpid: dpidProp,
  extractionBatchId,
}) => {
  const { dpid: dpidParam } = useParams<{ dpid: string }>();
  const dpid = dpidProp || dpidParam;
  const setValidationDismissalDialog = useSetAtom(validationDismissalDialogAtom);
  const [showDismissed, setShowDismissed] = useState(false);

  const filteredErrors = useMemo(
    () => (showDismissed ? errors : errors.filter((e) => !e.dismissalReason)),
    [errors, showDismissed]
  );

  const dismissedCount = useMemo(
    () => errors.filter((e) => !!e.dismissalReason).length,
    [errors]
  );

  const handleDismissClick = (error: TransformedValidationErrorRow) => {
    if (!dpid) return;
    setValidationDismissalDialog({
      isOpen: true,
      dataPoolId: dpid,
      validationId: error.id,
      extractionId: error.extractionId,
      field: error.field,
      currentDismissalReason: error.dismissalReason ?? null,
      error: error.error ?? null,
      errorDetail: error.errorDetail ?? null,
      isSingleExtractionMode: true,
    });
  };

  return (
    <Box sx={{ mt: 5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Transformed Validation Errors:
        </Typography>
        <Chip
          size="small"
          label={`${totalCount}`}
          color={totalCount > 0 ? 'error' : 'success'}
          sx={{
            fontWeight: 600,
            height: 18,
            '& .MuiChip-label': {
              padding: '0 6px',
              lineHeight: '18px',
              fontSize: '0.75rem',
            },
          }}
        />
        {dismissedCount > 0 && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showDismissed}
                onChange={(e) => setShowDismissed(e.target.checked)}
                data-cy="checkbox-show-dismissed"
              />
            }
            label={
              <Typography variant="body2">
                Show dismissed ({dismissedCount})
              </Typography>
            }
            sx={{ ml: 1 }}
          />
        )}
      </Box>
      <TableContainer
        component={Paper}
        className={TableStyles.documentsTablePaper}
        sx={{ maxHeight: '40vh' }}
      >
        <Table size="small" className={TableStyles.documentsTable}>
          <TableHead className={TableStyles.documentsTableHead}>
            <TableRow className={TableStyles.documentsTableHeadRow}>
              <TableCell className={TableStyles.documentsTableHeadCell} align="center">
                Extraction ID
              </TableCell>
              <TableCell className={TableStyles.documentsTableHeadCell} align="center">
                Field
              </TableCell>
              <TableCell className={TableStyles.documentsTableHeadCell} align="center">
                Error
              </TableCell>
              <TableCell className={TableStyles.documentsTableHeadCell} align="center">
                Error Details
              </TableCell>
              {showDismissed && (
                <TableCell className={TableStyles.documentsTableHeadCell} align="center">
                  Dismissal Reason
                </TableCell>
              )}
              <TableCell className={TableStyles.documentsTableHeadCell} align="center">
                Dismiss
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredErrors.length === 0 ? (
              <TableRow data-cy="transformed-validation-errors-empty">
                <TableCell colSpan={showDismissed ? 6 : 5} align="center">
                  <Typography variant="body2">No transformed validation errors found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredErrors.map((error, index) => (
                <TableRow
                  key={index}
                  className={TableStyles.documentsTableRow}
                  data-cy={`transformed-validation-error-row-${index}`}
                  sx={error.dismissalReason ? { opacity: 0.6 } : undefined}
                >
                  <TableCell className={TableStyles.documentsTableRowCell} align="center">
                    <LinkTableStyled
                      to={`/data-pools/${dpid}/extraction-batch/${extractionBatchId}/review/${error.extractionId}`}
                    >
                      {error.extractionId}
                    </LinkTableStyled>
                  </TableCell>
                  <TableCell className={TableStyles.documentsTableRowCell} align="center">
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {error.field}
                    </Typography>
                  </TableCell>
                  <TableCell className={TableStyles.documentsTableRowCell} align="center">
                    <Box>
                      {error.error && (
                        <Typography variant="body2" color="error" sx={{ mb: 0.5 }}>
                          {error.error}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell className={TableStyles.documentsTableRowCell} align="left">
                    <Box>
                      {error.errorDetail && (
                        <Typography variant="body2">{error.errorDetail}</Typography>
                      )}
                    </Box>
                  </TableCell>
                  {showDismissed && (
                    <TableCell className={TableStyles.documentsTableRowCell} align="center">
                      <Typography variant="body2">
                        {error.dismissalReason ?? ''}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell className={TableStyles.documentsTableRowCell} align="center">
                    <Tooltip title={error.dismissalReason ? `Dismissed: ${error.dismissalReason}` : 'Dismiss error'}>
                      <IconButton
                        size="small"
                        onClick={() => handleDismissClick(error)}
                        data-cy={`btn-dismiss-transformed-error-${index}`}
                      >
                        <DoNotDisturbOnOutlinedIcon
                          sx={{ color: error.dismissalReason ? 'warning.main' : 'error.main', fontSize: 20 }}
                        />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TransformedValidationErrorsTable;
